/**
 * SettingsShell — shared layout for Talent / Client / Agency Settings hubs.
 *
 * `variant="light"` (default) keeps the original light chrome (Client hub).
 * `variant="dark"` matches the Admin Command Center: slate-950 canvas,
 * glassy slate-900 cards, cyan/teal accents, glow hovers.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { formatDate } from '../../utils/timeFormat';
import {
  ArrowLeft, Loader2, Check, AlertCircle, WifiOff, Lock,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const ROLE_LABEL = { talent: 'Talent', client: 'Client', agency: 'Agency' };
const ROLE_HOME = { talent: '/talent/home', client: '/client/home', agency: '/agency/home' };

const V = {
  light: {
    page: 'min-h-screen bg-gray-50',
    header: 'bg-white border-b border-gray-200 sticky top-0 z-30',
    title: 'text-lg font-semibold text-gray-900 leading-tight',
    email: 'text-xs text-gray-500 truncate flex items-center gap-2',
    roleBadge: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
    back: 'text-gray-600',
    tabActive: 'bg-cyan-600 text-white border-cyan-600',
    tabIdle: 'bg-white text-gray-700 border-gray-200 hover:border-cyan-300',
    navActive: 'bg-cyan-50 text-cyan-900 font-medium',
    navIdle: 'text-gray-700 hover:bg-gray-100',
  },
  dark: {
    page: 'min-h-screen bg-slate-950 text-slate-100',
    header: 'bg-slate-950/80 backdrop-blur-md border-b border-slate-800/60 sticky top-0 z-30',
    title: 'text-lg font-bold text-slate-100 leading-tight tracking-tight',
    email: 'text-xs text-slate-400 truncate flex items-center gap-2',
    roleBadge: 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/40',
    back: 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/60',
    tabActive:
      'bg-gradient-to-r from-cyan-500/25 to-teal-500/15 text-cyan-200 border-cyan-500/40 shadow-[0_0_14px_rgba(34,211,238,0.18)]',
    tabIdle:
      'bg-slate-900/60 text-slate-300 border-slate-800/70 hover:border-cyan-500/40 hover:text-slate-100',
    navActive:
      'bg-gradient-to-r from-cyan-500/20 to-teal-500/10 text-cyan-200 font-semibold border border-cyan-500/30 shadow-[0_0_16px_rgba(34,211,238,0.12)]',
    navIdle:
      'text-slate-400 border border-transparent hover:bg-slate-900/70 hover:text-slate-100',
  },
};

const formatRelativeTime = (date) => {
  if (!date) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return formatDate(date);
};

const SaveStatusBadge = ({ status, lastSavedAt, onRetry }) => {
  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600" data-testid="settings-save-status-saving">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
      </span>
    );
  }
  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid="settings-save-status-saved">
        <Check className="w-3.5 h-3.5" /> Saved {lastSavedAt ? `· ${formatRelativeTime(lastSavedAt)}` : ''}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <button type="button" onClick={onRetry} className="inline-flex items-center gap-1.5 text-xs text-rose-700 hover:underline" data-testid="settings-save-status-error">
        <AlertCircle className="w-3.5 h-3.5" /> Save failed — retry
      </button>
    );
  }
  if (status === 'offline') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-700" data-testid="settings-save-status-offline">
        <WifiOff className="w-3.5 h-3.5" /> Offline — will sync
      </span>
    );
  }
  if (status === 'reauth') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-cyan-700" data-testid="settings-save-status-reauth">
        <Lock className="w-3.5 h-3.5" /> Confirm password to continue
      </span>
    );
  }
  if (status === 'dirty') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" data-testid="settings-save-status-dirty">
        Unsaved changes
      </span>
    );
  }
  return null;
};

export const SettingsShell = ({
  role,
  sections = [],
  activeSection,
  onSectionChange,
  saveStatus,
  lastSavedAt,
  onRetrySave,
  variant = 'light',
  children,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleLabel = ROLE_LABEL[role] || (user?.role ? ROLE_LABEL[user.role] : 'Account');
  const homePath = ROLE_HOME[role] || '/';
  const t = V[variant] || V.light;
  const dark = variant === 'dark';

  return (
    <div className={t.page} data-testid={`settings-shell-${role}`}>
      {/* Ambient glow (dark only) */}
      {dark && (
        <div aria-hidden="true" className="pointer-events-none fixed inset-x-0 top-0 h-64 bg-gradient-to-b from-cyan-500/10 via-transparent to-transparent" />
      )}

      {/* Header */}
      <header className={t.header}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(homePath)} className={t.back} data-testid="settings-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className={t.title}>Account Settings</h1>
            <div className={t.email}>
              <span className="truncate">{user?.email}</span>
              <Badge className={t.roleBadge}>{roleLabel}</Badge>
            </div>
          </div>
          <SaveStatusBadge status={saveStatus} lastSavedAt={lastSavedAt} onRetry={onRetrySave} />
        </div>
      </header>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-5">
        {/* Mobile section tabs — horizontal scroll. */}
        <nav className="md:hidden -mx-4 px-4 mb-5 overflow-x-auto scrollbar-none" aria-label="Settings sections (mobile)" data-testid="settings-mobile-tabs">
          <ul className="inline-flex gap-2 whitespace-nowrap pb-1">
            {sections.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => onSectionChange?.(s.id)}
                  aria-current={activeSection === s.id ? 'true' : undefined}
                  className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition-all active:scale-95
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70
                    ${activeSection === s.id ? t.tabActive : t.tabIdle}`}
                  data-testid={`settings-tab-${s.id}`}
                >
                  {s.icon && <s.icon className="w-3.5 h-3.5 mr-1 inline -mt-0.5" />}
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="md:grid md:grid-cols-[230px_1fr] md:gap-8">
          {/* Desktop sidebar nav. */}
          <aside className="hidden md:block" aria-label="Settings sections" data-testid="settings-sidebar">
            <ul className="space-y-1.5 sticky top-20">
              {sections.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => onSectionChange?.(s.id)}
                    aria-current={activeSection === s.id ? 'true' : undefined}
                    className={`w-full text-left flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm transition-all
                      focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70
                      ${activeSection === s.id ? t.navActive : t.navIdle}`}
                    data-testid={`settings-sidebar-${s.id}`}
                  >
                    {s.icon && <s.icon className="w-4 h-4" />}
                    <span className="flex-1">{s.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Main content slot — module renders here. */}
          <main className="min-w-0" data-testid="settings-main">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default SettingsShell;
